import { MAX_CHAT_LENGTH } from '../net/protocol';

/**
 * Herní chat.
 *
 * Dvě věci, které chat ve hře s pointer lockem vždycky rozbijí, když se na
 * ně zapomene:
 *
 *  - **Psaní nesmí hýbat postavou.** Při otevřeném vstupu se uvolní zámek
 *    kurzoru a stisky kláves se zastaví dřív, než se dostanou k ovládání.
 *  - **Zprávy musí být čitelné i bez otevřeného chatu.** Poslední řádky
 *    proto zůstávají chvíli vidět a teprve pak zmizí.
 */
export class ChatPanel {
  private readonly root: HTMLElement;
  private readonly log: HTMLElement;
  private readonly input: HTMLInputElement;
  private open = false;
  private fadeTimer: number | null = null;

  constructor(
    parent: HTMLElement,
    private readonly onSend: (message: string) => void,
  ) {
    this.root = document.createElement('div');
    this.root.id = 'chat';
    this.root.innerHTML = `
      <div class="chat-log" id="chat-log"></div>
      <form class="chat-form" id="chat-form">
        <input class="chat-input" id="chat-input" type="text" autocomplete="off"
               maxlength="${MAX_CHAT_LENGTH}" placeholder="Napiš zprávu…" />
      </form>
    `;
    parent.appendChild(this.root);

    this.log = this.root.querySelector('#chat-log')!;
    this.input = this.root.querySelector('#chat-input')!;

    this.root.querySelector<HTMLFormElement>('#chat-form')!.onsubmit = (event) => {
      event.preventDefault();
      const text = this.input.value.trim();
      this.input.value = '';
      if (text) this.onSend(text);
      this.close();
    };

    // Zachytáváme ve fázi capture, aby se stisky nedostaly k ovládání postavy.
    window.addEventListener('keydown', this.onKeyDown, true);
  }

  get isOpen(): boolean {
    return this.open;
  }

  addMessage(name: string, text: string, own = false): void {
    const line = document.createElement('div');
    line.className = own ? 'chat-line own' : 'chat-line';

    const author = document.createElement('span');
    author.className = 'chat-author';
    author.textContent = name;
    const body = document.createElement('span');
    body.textContent = text;

    line.append(author, body);
    this.log.appendChild(line);

    // Historie se drží krátká — starší řádky stejně nikdo nedohledává.
    while (this.log.childElementCount > 60) this.log.firstElementChild?.remove();
    this.log.scrollTop = this.log.scrollHeight;
    this.reveal();
  }

  addSystem(text: string): void {
    const line = document.createElement('div');
    line.className = 'chat-line system';
    line.textContent = text;
    this.log.appendChild(line);
    while (this.log.childElementCount > 60) this.log.firstElementChild?.remove();
    this.log.scrollTop = this.log.scrollHeight;
    this.reveal();
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown, true);
    if (this.fadeTimer !== null) window.clearTimeout(this.fadeTimer);
    this.root.remove();
  }

  // --- vnitřní ----------------------------------------------------------

  private onKeyDown = (event: KeyboardEvent): void => {
    if (!this.open) {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.openInput();
      }
      return;
    }

    // Zatímco je chat otevřený, klávesy patří jemu.
    event.stopPropagation();
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }
  };

  private openInput(): void {
    this.open = true;
    this.root.classList.add('open');
    // Zámek kurzoru se musí uvolnit, jinak by hráč psal naslepo a myš
    // by mu mezitím točila kamerou.
    document.exitPointerLock?.();
    this.input.focus();
    this.reveal();
  }

  private close(): void {
    this.open = false;
    this.root.classList.remove('open');
    this.input.blur();
    this.scheduleFade();
  }

  private reveal(): void {
    this.root.classList.add('visible');
    this.scheduleFade();
  }

  private scheduleFade(): void {
    if (this.fadeTimer !== null) window.clearTimeout(this.fadeTimer);
    if (this.open) return;
    this.fadeTimer = window.setTimeout(() => {
      this.root.classList.remove('visible');
    }, 8000);
  }
}
